/**
 * Descobre a chave de login fornecida pelo sistema existente.
 *
 * São aceitas três formas, em ordem de prioridade:
 *
 * 1. `?key=` (ou `?token=` / `?session=`) na URL do iframe — o caminho normal;
 * 2. `postMessage` do documento pai, para quando o host preferir não expor a
 *    chave na URL (ela apareceria no histórico e nos logs de acesso);
 * 3. `VITE_DEV_KEY`, apenas em desenvolvimento fora do iframe.
 */

const KEY_PARAMS = ['key', 'token', 'session', 'sessionKey'];
const HANDSHAKE_TIMEOUT_MS = 4000;

export function readKeyFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);

  for (const name of KEY_PARAMS) {
    const value = params.get(name);
    if (value?.trim()) return value.trim();
  }

  // Alguns hosts passam a chave no fragmento para ela não ir ao servidor.
  if (window.location.hash.length > 1) {
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    for (const name of KEY_PARAMS) {
      const value = hashParams.get(name);
      if (value?.trim()) return value.trim();
    }
  }

  return null;
}

export function isEmbedded(): boolean {
  return window.parent !== window;
}

/**
 * Pede a chave ao documento pai e espera a resposta.
 *
 * A origem de quem responde não é validada aqui de propósito: a chave só tem
 * valor se o backend a aceitar, e é ele que conhece o sistema de origem. O que
 * o host precisa garantir é o `frame-ancestors` — configurado na API.
 */
export function requestKeyFromParent(): Promise<string | null> {
  if (!isEmbedded()) return Promise.resolve(null);

  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMessage);
      resolve(null);
    }, HANDSHAKE_TIMEOUT_MS);

    function onMessage(event: MessageEvent): void {
      const data = event.data as { type?: string; key?: string } | null;
      if (!data || data.type !== 'supporchat:session-key') return;

      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      resolve(typeof data.key === 'string' && data.key.trim() ? data.key.trim() : null);
    }

    window.addEventListener('message', onMessage);
    window.parent.postMessage({ type: 'supporchat:request-session-key' }, '*');
  });
}

export async function resolveSessionKey(): Promise<string | null> {
  const fromUrl = readKeyFromUrl();
  if (fromUrl) return fromUrl;

  const fromParent = await requestKeyFromParent();
  if (fromParent) return fromParent;

  const devKey = import.meta.env.VITE_DEV_KEY as string | undefined;
  return devKey?.trim() ?? null;
}

/** Avisa o host que o painel carregou — útil para ajustar a altura do iframe. */
export function notifyParentReady(): void {
  if (!isEmbedded()) return;
  window.parent.postMessage({ type: 'supporchat:ready' }, '*');
}
